// ========================================
// components/Layout.js - Global Layout Component
// ========================================
// LAST MODIFIED: April 12, 2026
// 
// FIXES:
// - Logo always links to Landing page (/) - industry standard
// - Added "Home" link to navigation
// - Updated Features/Pricing to use new dedicated pages
// ========================================

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import Footer from './Footer';

const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  // ========================================
  // CHECK LOGIN STATUS
  // ========================================
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    
    if (token && userStr) {
      setIsLoggedIn(true);
      try {
        const user = JSON.parse(userStr);
        setUserName(user.fullName?.split(' ')[0] || 'User');
      } catch (e) {
        setUserName('User');
      }
    } else {
      setIsLoggedIn(false);
      setUserName('');
    }
  }, [location]);

  // ========================================
  // HANDLE LOGOUT
  // ========================================
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    navigate('/');
  };

  // ========================================
  // CLOSE MOBILE MENU
  // ========================================
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const closeMenu = () => setMobileMenuOpen(false);

  // ========================================
  // CHECK IF PATH IS ACTIVE (for highlighting)
  // ========================================
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path;
  };

  // ========================================
  // DETERMINE IF CURRENT PAGE IS DASHBOARD OR PROTECTED PAGE
  // ========================================
  const isDashboardPage = () => {
    const dashboardPaths = [
      '/dashboard', '/add-asset', '/asset/', '/beneficiaries', 
      '/next-of-kin', '/trustees', '/upgrade', '/settings'
    ];
    return dashboardPaths.some(path => location.pathname.startsWith(path));
  };

  // ========================================
  // PUBLIC NAVIGATION LINKS (includes Home)
  // ========================================
  const publicNavLinks = [
  { name: 'Features', href: '/features' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'FAQ', href: '/faq' },
  { name: 'About', href: '/about' },
  { name: 'Contact', href: '/contact' }
];

  // ========================================
  // DASHBOARD NAVIGATION LINKS
  // ========================================
  const dashboardNavLinks = [
    { name: 'Dashboard', href: '/dashboard' },
    { name: 'Add Asset', href: '/add-asset' },
    { name: 'Next of Kin', href: '/next-of-kin' },
    { name: 'Trustees', href: '/trustees' },
    { name: 'Beneficiaries', href: '/beneficiaries' },
    { name: 'Upgrade', href: '/upgrade' },
    { name: 'Settings', href: '/settings' }
  ];

  const showDashboardNav = isDashboardPage();
  const navLinks = showDashboardNav ? dashboardNavLinks : publicNavLinks;

  return (
    <div className="layout-container">
      
      <header className="layout-header">
        <div className="layout-header-inner">
          
          {/* Logo - ALWAYS links to Landing page (industry standard) */}
          <Link to="/" className="layout-logo">
            <img src="/images/symbols/logo-light.png" alt="Agyapadie Vault" />
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="layout-nav-desktop">
            {navLinks.map((link) => (
              <Link 
                key={link.name} 
                to={link.href} 
                className={`layout-nav-link ${isActive(link.href) ? 'active' : ''}`}
                onClick={closeMenu}
              >
                {link.name}
              </Link>
            ))}
          </nav>
          
          {/* Desktop Auth Buttons */}
          <div className="layout-auth-desktop">
            {isLoggedIn ? (
              <>
                {!showDashboardNav && (
                  <Link to="/dashboard" className="layout-btn-dashboard">📊 Dashboard</Link>
                )}
                <span className="layout-user-greeting">👋 {userName}</span>
                <button onClick={handleLogout} className="layout-btn-logout">Logout</button>
              </>
            ) : (
              <>
                <Link to="/login" className="layout-btn-login">Log In</Link>
                <Link to="/register" className="layout-btn-signup">Sign Up</Link>
              </>
            )}
          </div>
          
          {/* Mobile Hamburger Button */}
          <button 
            className="layout-mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
          
        </div>
        
        {/* Mobile Navigation Menu */}
        <div className={`layout-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
          {navLinks.map((link) => (
            <Link 
              key={link.name} 
              to={link.href} 
              className={`layout-mobile-nav-link ${isActive(link.href) ? 'active' : ''}`}
              onClick={closeMenu}
            >
              {link.name}
            </Link>
          ))}
        </div>
        
        {/* Mobile Action Bar - CRITICAL BUTTONS ALWAYS VISIBLE */}
        <div className="layout-mobile-actions">
          {isLoggedIn ? (
            <>
              {!showDashboardNav && (
                <Link to="/dashboard" className="mobile-action-btn dashboard">📊 Dashboard</Link>
              )}
              <span className="mobile-user-greeting">👋 {userName}</span>
              <button onClick={handleLogout} className="mobile-action-btn logout">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" className="mobile-action-btn login">Log In</Link>
              <Link to="/register" className="mobile-action-btn signup">Sign Up</Link>
            </>
          )}
        </div>
      </header>

      <main className="layout-main">
        <Outlet />
      </main>

      <Footer />

      <style dangerouslySetInnerHTML={{ __html: `
        
        .layout-container {
          font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
        }
        
        .layout-main { flex: 1; }
        
        .layout-header {
          background: #0A0A0A;
          border-bottom: 2px solid #FFD700;
          position: sticky;
          top: 0;
          z-index: 1000;
        }
        
        .layout-header-inner {
          max-width: 1200px;
          margin: 0 auto;
          padding: 15px 40px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .layout-logo img { height: 45px; width: auto; }
        
        .layout-nav-desktop {
          display: flex;
          gap: 25px;
        }
        
        .layout-nav-link {
          color: #F5F0E6;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: color 0.3s;
          padding: 5px 0;
        }
        
        .layout-nav-link:hover { color: #FFD700; }
        
        .layout-nav-link.active {
          color: #FFD700;
          border-bottom: 2px solid #FFD700;
        }
        
        .layout-auth-desktop {
          display: flex;
          gap: 12px;
          align-items: center;
        }
        
        .layout-user-greeting {
          color: #FFD700;
          font-size: 14px;
          font-weight: 500;
        }
        
        .layout-btn-login {
          padding: 8px 20px;
          background: transparent;
          border: 1px solid #FFD700;
          color: #FFD700;
          border-radius: 5px;
          text-decoration: none;
          font-size: 13px;
          transition: all 0.3s;
        }
        
        .layout-btn-login:hover {
          background: #FFD700;
          color: #0A0A0A;
        }
        
        .layout-btn-signup {
          padding: 8px 20px;
          background: #FFD700;
          border: none;
          color: #0A0A0A;
          border-radius: 5px;
          text-decoration: none;
          font-size: 13px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .layout-btn-signup:hover {
          background: #B8860B;
        }
        
        .layout-btn-dashboard {
          padding: 8px 20px;
          background: #2E7D32;
          border: none;
          color: white;
          border-radius: 5px;
          text-decoration: none;
          font-size: 13px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .layout-btn-dashboard:hover {
          background: #1B5E20;
        }
        
        .layout-btn-logout {
          padding: 8px 20px;
          background: transparent;
          border: 1px solid #dc2626;
          color: #dc2626;
          border-radius: 5px;
          cursor: pointer;
          font-size: 13px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .layout-btn-logout:hover {
          background: #dc2626;
          color: white;
        }
        
        .layout-mobile-menu-btn {
          display: none;
          background: none;
          border: none;
          color: #FFD700;
          font-size: 28px;
          cursor: pointer;
          padding: 8px;
          min-width: 44px;
        }
        
        .layout-mobile-menu {
          display: none;
          flex-direction: column;
          background: #0A0A0A;
          padding: 12px 20px;
          gap: 8px;
          border-top: 1px solid #FFD700;
        }
        
        .layout-mobile-menu.open { display: flex; }
        
        .layout-mobile-nav-link {
          color: #F5F0E6;
          text-decoration: none;
          font-size: 16px;
          padding: 8px 12px;
          border-radius: 5px;
          transition: all 0.3s;
        }
        
        .layout-mobile-nav-link:hover { background: #333; }
        
        .layout-mobile-nav-link.active {
          color: #FFD700;
          background: rgba(255,215,0,0.1);
        }
        
/* Mobile Action Bar - Premium design matching Landing page */
.layout-mobile-actions {
  display: none;
  justify-content: flex-end;
  align-items: center;
  padding: 10px 20px;
  background: #1A1A1A;
  border-top: 1px solid #FFD700;
  gap: 15px;
}

.mobile-action-btn {
  padding: 8px 20px;
  border-radius: 5px;
  text-align: center;
  text-decoration: none;
  font-size: 13px;
  font-weight: bold;
  transition: all 0.3s;
  display: inline-block;
  width: auto;
  min-height: auto;
}

.mobile-action-btn.login {
  background: transparent;
  border: 1px solid #FFD700;
  color: #FFD700;
}

.mobile-action-btn.signup {
  background: #FFD700;
  color: #0A0A0A;
}

.mobile-action-btn.dashboard {
  background: #2E7D32;
  color: white;
}

.mobile-action-btn.logout {
  background: transparent;
  border: 1px solid #dc2626;
  color: #dc2626;
}

.mobile-user-greeting {
  color: #FFD700;
  font-size: 13px;
  font-weight: 500;
  margin-right: auto;
}
        
        @media (max-width: 1024px) {
          .layout-nav-desktop { gap: 20px; }
          .layout-header-inner { padding: 15px 25px; }
        }
        
        @media (max-width: 768px) {
          .layout-nav-desktop { display: none; }
          .layout-auth-desktop { display: none; }
          .layout-mobile-menu-btn { display: block; }
          .layout-mobile-actions { display: flex; }
          .layout-header-inner { padding: 12px 20px; }
        }
        
      `}} />
      
    </div>
  );
};

export default Layout;